import React, { useState, useEffect } from "react";
import { Form, Button, Col, Row, FormControl, FormLabel } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getAllCategory } from "../../api/categoryAPI";
import { Delete } from '@mui/icons-material';
import { IconButton } from "@mui/material";
import Slider from "react-slick";

const settings = {
  dots: true,
  infinite: false,
  speed: 500,
  className: "center",
  centerMode: true,
};

export const _baseURL = process.env.REACT_APP_IMG_URL;

const ProductForm = ({ onSubmit, productData }) => {
  const [categories, setCategories] = useState([]);
  const [imageFiles, setImageFiles] = useState([]); // To store the actual image files
  const [imagePreviews, setImagePreviews] = useState([]); // To store image preview URLs

  // Fetch categories from API on component mount
  useEffect(() => {
    (async () => {
      const response = await getAllCategory();
      setCategories(response);
    })();
  }, []);

  // Handle image selection and preview generation
  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files); // Store the image files in state

    // Generate previews for the selected files
    const filePreviews = files.map((file) => URL.createObjectURL(file));
    setImagePreviews(filePreviews);
  };

  // Handle image deletion
  const handleImageDelete = (index) => {
    const updatedFiles = imageFiles.filter((_, i) => i !== index);
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index);
    setImageFiles(updatedFiles);
    setImagePreviews(updatedPreviews);
  };

  // Formik setup with validation schema using Yup
  const formik = useFormik({
    initialValues: {
      title: productData?.title || "",
      description: productData?.description || "",
      price: productData?.price || 0,
      discounted: productData?.discounted || 0,
      categoryID: productData?.categoryID || "",
      category: productData?.category || "",
      quantity: productData?.quantity || 0,
      productQuantity: productData?.productQuantity || "",
      brand: productData?.brand || "",
      bestSeller: productData?.bestSeller || false,
      isItNew: productData?.isItNew || false,
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      price: Yup.number().required("Price is required").positive("Price must be positive"),
      quantity: Yup.number().required("Quantity is required"),
      categoryID: Yup.string().required("Category is required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("price", values.price);
      formData.append("discounted", values.discounted);
      formData.append("categoryID", values.categoryID);
      formData.append("quantity", values.quantity);
      formData.append("productQuantity", values.productQuantity);
      formData.append("brand", values.brand);
      formData.append("bestSeller", values.bestSeller);
      formData.append("isItNew", values.isItNew);

      imageFiles.forEach((image) => {
        formData.append("images", image);
      });
      onSubmit(formData);
    },
  });

  return (
    <Form onSubmit={formik.handleSubmit}>
      <h3>{productData ? `Edit Product (ID: ${productData._id})` : "Create Product"}</h3>

      {productData && (
        <Form.Group as={Row} className="mb-3" controlId="formProductId">
          <Form.Label column sm={2}>
            Product ID
          </Form.Label>
          <Col sm={10}>
            <FormControl
              plaintext
              readOnly
              defaultValue={productData._id}
            />
          </Col>
        </Form.Group>
      )}

      <Form.Group className="mb-3">
        <FormLabel>Title</FormLabel>
        <FormControl
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          isInvalid={!!formik.errors.title}
        />
        <FormControl.Feedback type="invalid">{formik.errors.title}</FormControl.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <FormLabel>Description</FormLabel>
        <FormControl
          as="textarea"
          rows={3}
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
        />
      </Form.Group>

      <div className="slider-container">
        <label>Product Images</label>
        <div style={{ width: '400px' }}>
          <Slider {...settings}>
            {productData?.image && productData.image.map((image) => {
              return (
                <div key={image._id}>
                  <img src={`${_baseURL + 'upload/' + image.data}`} alt="product image" style={{ width: '200px', height: '200px' }} />
                </div>
              )
            })}
          </Slider>
        </div>
      </div>

      <Form.Group className="mb-3">
        <FormLabel>Price</FormLabel>
        <FormControl
          name="price"
          value={formik.values.price}
          onChange={formik.handleChange}
          isInvalid={!!formik.errors.price}
        />
        <FormControl.Feedback type="invalid">{formik.errors.price}</FormControl.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <FormLabel>Discounted Price</FormLabel>
        <FormControl
          name="discounted"
          value={formik.values.discounted}
          onChange={formik.handleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <FormLabel>Category</FormLabel>
        <FormControl
          as="select"
          name="categoryID"
          value={formik.values.categoryID}
          onChange={formik.handleChange}
          isInvalid={!!formik.errors.categoryID}
        >
          <option value="">Select Category</option>
          {categories?.map((category) => (
            <option key={category._id} value={category._id}>
              {category.category}
            </option>
          ))}
        </FormControl>
        <FormControl.Feedback type="invalid">{formik.errors.categoryID}</FormControl.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <FormLabel>Quantity</FormLabel>
        <FormControl
          
          name="quantity"
          value={formik.values.quantity}
          onChange={formik.handleChange}
          isInvalid={!!formik.errors.quantity}
        />
        <FormControl.Feedback type="invalid">{formik.errors.quantity}</FormControl.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <FormLabel>Product Quantity (Only on Grams ex: 250g)</FormLabel>
        <FormControl
          name="productQuantity"
          value={formik.values.productQuantity}
          onChange={formik.handleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <FormLabel>Brand</FormLabel>
        <FormControl
          name="brand"
          value={formik.values.brand}
          onChange={formik.handleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          label="Best Seller"
          name="bestSeller"
          checked={formik.values.bestSeller}
          onChange={formik.handleChange}
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Check
          type="switch"
          label="Is It New?"
          name="isItNew"
          checked={formik.values.isItNew}
          onChange={formik.handleChange}
        />
      </Form.Group>

      {/* Image Upload Section */}
      <Form.Group className="mb-3">
        <FormLabel>Upload Images</FormLabel>
        <FormControl
          type="file"
          name="images"
          onChange={handleImageChange}
          multiple
          accept="image/*"
        />
      </Form.Group>

      {/* Image Previews with Delete Icons */}
      {imagePreviews.length > 0 && (
        <Row className="my-4">
          {imagePreviews.map((image, index) => (
            <Col key={index} sm={3} style={{ position: "relative", maxWidth: "200px", maxHeight: "300px" }}>
              <img
                src={image}
                alt={`Preview ${index}`}
                style={{ width: "200px", height: "200px", objectFit: "cover", borderRadius: "8px" }}
              />
              <IconButton style={{
                position: "absolute",
                top: 0,
                let: 0,
                color: "red",
                cursor: "pointer",
                backgroundColor: "white",
                borderRadius: "50%",
                fontSize: "20px",
              }} onClick={() => handleImageDelete(index)}>
                <Delete />
              </IconButton>
            </Col>
          ))}
        </Row>
      )}

      <Button type="submit" variant="primary" >
        {productData ? "Update Product" : "Create Product"}
      </Button>
    </Form>
  );
};

export default ProductForm;
